import React, { useContext, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Container, Row, Col } from 'react-bootstrap'
import {
  Table,
  PaginationContainer,
  SortAmountDown,
  SortAmountUp,
} from './style'
import { I18nContext } from 'i18n'
import {
  Permission,
  IconButton,
  ServerPagination,
  SearchTextContainer,
  BackdropLoader,
  ConfirmDialog,
  Input,
  MultiSelectCheckboxes,
  StandardCheckbox,
} from 'components'
import { FaPlus, FaEdit, FaRegTrashAlt } from 'react-icons/fa'
import {
  DEFAULT_PAGE_SIZE,
  DATE_MOMENT_FORMAT,
  ORDER_DIRECTION,
} from 'utils/constants'
import { get, startCase, isEmpty, orderBy } from 'lodash'
import classnames from 'classnames'
import moment from 'moment'
import CurrencyFormat from 'react-currency-format'
import ColumnResizer from 'react-column-resizer';

const AdminTable = ({
  columns,
  data,
  permissions,
  loading,
  defaultSort,
  scope,
  onAddClick,
  onDelete,
  onEdit,
  customComponents,
  customActions = [],
  server,
  onSort,
  onPageChange,
  meta,
  onMultiChange,
  selectable,
  onSelect,
  propDelete,
  onDeleteConfirmationText,
}) => {
  const ASC = server ? ORDER_DIRECTION.ASC :  ORDER_DIRECTION.ASC.toLocaleLowerCase()
  const DESC = server ? ORDER_DIRECTION.DESC :  ORDER_DIRECTION.DESC.toLocaleLowerCase()
  const { translate } = useContext(I18nContext)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [sortDirection, setSortDirection] = useState(ASC)
  const [sortColumn, setSortColumn] = useState(defaultSort)
  const [details, setDetails] = useState()
  const [filter, setFilter] = useState()
  const [showConfirm, setShowConfirm] = useState(false)
  const [deleteId, setDeleteId] = useState()
  const [selectedOptions, setSelectedOptions] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  
  const caseScope = startCase(scope)
  const [title, setTitle] = useState(`Delete ${caseScope}`)
  const subtitle = onDeleteConfirmationText || `Are you sure you want to delete this ${caseScope}?`;

  useEffect(() => {
    setDetails(data)
  }, [data])

  useEffect(() => {
    if (!isEmpty(filter)) {
      let filtered = data
      Object.entries(filter).forEach(([key, value]) => {
        filtered = filtered.filter((item) =>
          item[key]?.toLowerCase().includes(value.toLowerCase())
        )
      })

      setDetails(filtered)
      setPage(1)
    }
  }, [filter])

  useEffect(() => {
    setShowConfirm(!!deleteId)
  }, [deleteId])

  useEffect(() => {
    if (onMultiChange) onMultiChange(selectedOptions)
  }, [selectedOptions])

  useEffect(() => {
    if (onSelect) onSelect(selectedRows)
  }, [selectedRows])

  const handleFilter = (r, filter) => {
    const newPage = filter?.page || page
    const newSize = filter?.pageSize || pageSize
    setPageSize(newSize)
    setPage(newPage)

    if (server) onPageChange(+newPage, +newSize)
  }

  const paginate = (array, size, pageNumber) =>
    array?.slice((pageNumber - 1) * size, pageNumber * size)

  const sort = (array, field, direction) =>
    orderBy(array, [(list) => list?.[field]?.toLowerCase()], [direction])

  const paginated = server ? details : paginate(details, pageSize, page)

  const handleSort = (column) => {
    let newOrder = sortDirection === DESC ? ASC : DESC
    if (column !== sortColumn) newOrder = ASC
    setSortDirection(newOrder)
    setSortColumn(column)

    if (server) {
      onSort(column, newOrder)
      return
    }

    setDetails(sort(details, column, newOrder))
    setPage(1)
  }

  const renderSort = (column) => {
    if (column === sortColumn) {
      if (sortDirection === ASC) {
        return <SortAmountUp active="true" onClick={() => handleSort(column)} />
      } else {
        return (
          <SortAmountDown active="true" onClick={() => handleSort(column)} />
        )
      }
    } else {
      return <SortAmountDown onClick={() => handleSort(column)} />
    }
  }

  const handleFilterChange = ({ target: { value } }, name) => {
    setFilter({ ...filter, [name]: value })
  }

  const handleOndelete = (id) => {
    onDelete(deleteId)
    //Hide the prompt
    setDeleteId(null)
  }

  const keyColumn = columns.find(({ key }) => key)?.name

  const renderType = useCallback((value, type) => {
    switch (type) {
      case 'Date':
        const isReactElement = typeof(get(value, '$$typeof')) === 'symbol'
        return value && !isReactElement ?
          moment(value).format(DATE_MOMENT_FORMAT)
          : value
      case 'Currency':
        return value > 0 ? (
          <CurrencyFormat
            value={value}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
          />
        ) : ''
      default:
        return value
    }
  }, [])

  const handleMultiChange = useCallback(
    (options, column) => {
      setSelectedOptions({ [column]: options })
    },
    [setSelectedOptions]
  )

  const renderMulti = (options, column) => (
    <MultiSelectCheckboxes
      options={options}
      onChange={(options) => handleMultiChange(options, column)}
      className="ml-1"
      selectedOptions={selectedOptions[column]}
    />
  )

  const handleSelectRow = useCallback((checked, value) => {
    checked
      ? setSelectedRows(selectedRows.concat(value))
      : setSelectedRows(selectedRows.filter((row) => row !== value))
  })

  const handleSelectAll = useCallback((e) => {
    e.target.checked
      ? setSelectedRows(server ? data : paginated)
      : setSelectedRows([])
  })

  const handleDeleteClick = useCallback((row) => {
    setDeleteId(row?.[keyColumn])
    const displayName = row?.[propDelete]
    if (displayName) {
      setTitle(translate('admin_delete_title', { displayName, scope: caseScope }))
    }
  }, [setTitle, setDeleteId, caseScope])

  const renderDeleteIcon = useCallback((d) => {
    const widget = customComponents?.delete(d) ||
      <IconButton
        icon={<FaRegTrashAlt />}
        red
        className="mr-0 pr-0 ml-1 small"
        tooltip="Delete"
      />
    return React.cloneElement(widget, { onClick: () => handleDeleteClick(d) })
  }, [customComponents, handleDeleteClick])

  return (
    <Container fluid>
      <Row>
        {columns
          .filter(({ filter }) => filter)
          .map(({ name }) => (
            <Col md="2" key={`admin_filter_${name}`}>
              <SearchTextContainer>
                <Input
                  onChange={(e) => handleFilterChange(e, name)}
                  placeholder={translate(`admin_filter_${name}`)}
                  maxLength={100}
                />
              </SearchTextContainer>
            </Col>
          ))}

        <Col className="text-right">
          <Permission permission={[permissions?.add]}>
            <IconButton
              onClick={onAddClick}
              icon={<FaPlus />}
              text={translate(`admin_add_${scope}`)}
              className="mb-3"
            />
          </Permission>
        </Col>
      </Row>
      <Row>
        <Col>
          <Table size="sm">
            <thead>
              <tr>
                {
                  selectable && (
                    <th>
                      <StandardCheckbox
                        className="mr-0"
                        onChange={handleSelectAll}
                        checked={selectedRows?.length === data?.length}
                      />
                    </th>
                  )
                }
                {columns.map(
                  ({ name, sortable, key, hidden, multi, options, type, resizable, className }) =>
                    !hidden && (
                      <React.Fragment key={`admin_${scope}_${name}`}>
                        <th
                          className={classnames({
                            'd-none': key,
                          },'text-left', className)}
                        >
                          {sortable && renderSort(name)}
                          {translate(`admin_${scope}_${name}`)}
                          {multi && renderMulti(options, name)}
                        </th>
                        {resizable &&  <ColumnResizer className="columnResizer" minWidth={0} />}
                      </React.Fragment>
                    )
                )}
                <th style={{ width: `${customActions.length + 2}rem `, borderLeft: 'unset' }}/>
              </tr>
            </thead>
            <tbody>
              {paginated?.map((d, ix) => (
                <tr key={`column_${ix}`}>
                  {
                    selectable && (
                      <th>
                        <StandardCheckbox
                          className="mr-0"
                          onChange={(e) => handleSelectRow(e.target.checked, d)}
                          checked={selectedRows.find((row) => row === d) || false}
                        />
                      </th>
                    )
                  }
                  {columns.map(
                    ({ name, key, hidden, type, resizable }) =>
                      !hidden && (
                        <React.Fragment key={`column_${scope}_${name}`}>
                          <td
                            title={typeof d?.[name] === 'string' ? d?.[name] : null}
                            className={classnames({
                              'd-none': key,
                              'text-right': type === 'Currency',
                              'text-center': type === 'Date',
                              'adminTable-max-column': name === 'scholarshipMaximumAward',
                              'adminTable-max-column__date': name === 'openDate' || name === 'closeDate',
                              'adminTable-max-column__status': name === 'status'
                            })}
                          >
                            {renderType(d?.[name], type)}
                          </td>
                          {resizable && <td />}
                        </React.Fragment>
                      )
                  )}
                  <td style={{ textOverflow: 'unset' }} className="pl-0">
                    {
                      customActions.map((render, index) =>
                        <span key={index}>{render(d)}</span>)
                    }
                    <Permission permission={[permissions?.edit]}>
                      <IconButton
                        onClick={() => onEdit(d?.[keyColumn])}
                        icon={<FaEdit />}
                        className="pl-0 mr-0 pr-0 ml-1 small"
                        tooltip="Edit"
                      />
                    </Permission>
                    <Permission permission={[permissions?.delete]}>
                      {renderDeleteIcon(d)}
                    </Permission>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
      <Row>
        <Col>
          {details && (
            <PaginationContainer className="pl-2">
              <ServerPagination
                onChange={handleFilter.bind(null, false)}
                pagesCount={meta?.pagesCount || Math.ceil(details?.length / pageSize)}
                itemsCount={meta?.itemsCount || details?.length}
                loading={loading}
                selectedPage={page}
                pageSizes={[10, 25, 50, 100, 1000]}
              />
            </PaginationContainer>
          )}
        </Col>
      </Row>
      {loading && <BackdropLoader />}
      <ConfirmDialog
        show={showConfirm}
        onConfirm={() => handleOndelete(deleteId)}
        onCancel={() => setDeleteId(null)}
        body={subtitle}
        {...{ title }}
      />
    </Container>
  )
}

AdminTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  permissions: PropTypes.shape({
    add: PropTypes.string,
    edit: PropTypes.string,
    delete: PropTypes.string,
  }),
}

export default AdminTable
